<script setup lang="ts">
import { useDialogPluginComponent } from 'quasar';
import { useForm } from 'vee-validate';
import * as yup from 'yup';

import type { Locales, MailingCampaignDto } from '@/services/types/api.types';
import { useLanguageValidationErrors } from '@/composables';
import { wrapSavingOperation } from '@/helpers/notifications.helper';
import { localizableString } from '@/helpers/validation.helper';

import LanguageSwitcher from '@/components/LanguageSwitcher.vue';
import LocalizedInput from '@/components/LocalizedInput.vue';
import { useMailingCampaignStore } from '@/stores';
import { provideEditLocale } from '@/composables/data-display-utils';
import { toRef } from 'vue';

const mailingCampaignStore = useMailingCampaignStore();

// PROPS
const props = defineProps<{
  campaign: MailingCampaignDto;
  languageEnabled: Locales[];
}>();

// DIALOG SET-UP
defineEmits([...useDialogPluginComponent.emits]);
const { dialogRef, onDialogHide, onDialogOK, onDialogCancel } = useDialogPluginComponent();

// FORM
const { handleSubmit, errors, meta } = useForm({
  initialValues: props.campaign,
  validationSchema: yup.object<any>({
    title: localizableString(props.languageEnabled),
  }),
});
const languageErrors = useLanguageValidationErrors(errors);

const editLocale = provideEditLocale(toRef(() => props.languageEnabled));
const onSubmit = handleSubmit(async (values) => {
  await wrapSavingOperation(
    async () => await mailingCampaignStore.saveMailingCampaign(values),
    () => onDialogOK(),
  );
});
</script>
<template>
  <q-dialog ref="dialogRef" @hide="onDialogHide" persistent>
    <q-card class="q-dialog-plugin" v-if="props.campaign" style="width: 600px">
      <q-card-section class="row">
        <div class="text-h6">{{ $t('form.dialogs.campaign.create') }}</div>
        <q-space />
        <language-switcher
          v-model="editLocale"
          :has-validation-errors="languageErrors"
          :languageEnabled="languageEnabled"
        />
      </q-card-section>
      <q-separator />
      <q-card-section>
        <localized-input
          name="title"
          label="Titre"
          :editLocale="editLocale"
          outlined
          :language_enabled="languageEnabled"
        />
      </q-card-section>
      <q-separator />
      <q-card-actions align="right">
        <q-btn color="primary" flat :label="$t('global.cancel')" @click="onDialogCancel" />
        <q-btn color="primary" :label="$t('global.save')" @click="onSubmit" />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>
